import { useRef, useState, useLayoutEffect, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { BUSINESS_SETUP_QUESTIONS, BUSINESS_SETUP_FILE_ACCEPT, BusinessSetupTranscriptSchema, businessSetupComplete, type BusinessSetupAnswers, type BusinessSetupReply, type BusinessSetupDocument, type BusinessSetupTranscript } from '@tenfour/contracts'
import { Paperclip, FileText, FolderOpen, X } from 'lucide-react'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { api } from '@/lib/api'
import { ComposerSendButton } from '@/components/ComposerSendButton'
import { StreamedText } from './SignupConversation'
import { scrollConversationTo } from './scrollConversation'
import { ScribeButton } from '@/components/scribe/ScribeButton'
import { useBusinessDictation } from './useBusinessDictation'
import { useBusinessDocuments } from './useBusinessDocuments'
import { useStreamedText } from './useStreamedText'
import { useComposerSpace } from './useComposerSpace'
import c from './conversation.module.css'

const emptyAnswers: BusinessSetupAnswers = { work: '', area: '', preferences: '' }
export { emptyAnswers }

function BusinessQuestion({ text }: { text: string }) {
  const { visible } = useStreamedText(text, true, 'words')
  return <h2 className={c.memoryQuestion} aria-label={text}><span className={c.questionMeasure} aria-hidden="true">{text}</span><span className={c.questionVisible} aria-hidden="true">{visible}</span></h2>
}

export function BusinessConversation({ answers, documents, transcript, onDocumentsChange, onChange, onContinue, onSkip, saving = false }: {
  answers: BusinessSetupAnswers
  documents: BusinessSetupDocument[]
  onDocumentsChange: (documents: BusinessSetupDocument[]) => void | Promise<void>
  transcript: BusinessSetupTranscript
  onChange: (answers: BusinessSetupAnswers, projection?: BusinessSetupReply['projection'], transcript?: BusinessSetupTranscript) => void | Promise<void>
  onContinue: () => void
  onSkip: () => void
  saving?: boolean
}) {
  const [section, setSection] = useState(() => BUSINESS_SETUP_QUESTIONS.find(q => !answers[q.key].trim())?.key ?? null)
  const [message, setMessage] = useState('')
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [skipOpen, setSkipOpen] = useState(false)
  const uploads = useBusinessDocuments(documents, onDocumentsChange)
  const composer = useComposerSpace()
  const filesInput = useRef<HTMLInputElement>(null)
  const folderInput = useRef<HTMLInputElement>(null)
  const uploadMenu = useRef<HTMLDetailsElement>(null)
  function chooseFiles() { uploadMenu.current?.removeAttribute('open'); filesInput.current?.click() }
  function chooseFolder() { uploadMenu.current?.removeAttribute('open'); folderInput.current?.click() }
  function openSkip() { setSkipOpen(true) }
  function closeSkip() { setSkipOpen(false) }
  function confirmSkip() { setSkipOpen(false); onSkip() }
  function dictated(text: string) { setMessage(previous => previous ? `${previous} ${text}` : text) }
  const voice = useBusinessDictation(dictated, setError)
  const input = useRef<HTMLTextAreaElement>(null)
  const question = BUSINESS_SETUP_QUESTIONS.find(q => q.key === section)
  const currentTurn = useRef<HTMLDivElement>(null)
  const previousSection = useRef(section)
  useLayoutEffect(() => {
    if (reply || previousSection.current !== section) scrollConversationTo(currentTurn.current)
    previousSection.current = section
  }, [section, reply])
  const ready = !question && businessSetupComplete(answers)
  const locked = busy || saving || !!uploads.progress || voice.live || voice.pending

  async function send(event?: FormEvent) {
    event?.preventDefault()
    if (locked || (!message.trim() && !documents.length)) return
    setBusy(true); setError('')
    try {
      const result = await api<BusinessSetupReply>('/business-setup/turn', { method: 'POST', body: JSON.stringify({ answers, section: section ?? 'work', message, documents }) })
      const history: BusinessSetupTranscript = [...transcript,
        { role: 'ASSISTANT', content: question?.question ?? 'What else would you like me to know?' },
        { role: 'USER', content: [message.trim(), documents.length ? `Documents: ${documents.map(doc => doc.name).join(', ')}` : ''].filter(Boolean).join('\n\n') },
        ...(result.reply ? [{ role: 'ASSISTANT' as const, content: result.reply }] : []),
      ]
      if (!BusinessSetupTranscriptSchema.safeParse(history).success) throw new Error('Finish setup to continue this conversation in Aria.')
      await onChange(result.answers, result.projection, history)
      setReply(result.reply); setMessage('')
      setSection(BUSINESS_SETUP_QUESTIONS.find(q => !result.answers[q.key].trim())?.key ?? null)
    } catch (err) { setError(err instanceof Error ? err.message : 'Your answer is still here. Try sending it again.') }
    finally { setBusy(false); input.current?.focus({ preventScroll: true }) }
  }
  function keyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void send() }
  }
  function editAnswer(event: React.MouseEvent<HTMLButtonElement>) {
    const key = event.currentTarget.value as keyof BusinessSetupAnswers
    setSection(key); setMessage(answers[key]); setReply(''); input.current?.focus({ preventScroll: true })
  }
  function skipOptional() { setSection(null); setReply('We can fill in the rest as we go.') }
  function changeMessage(event: React.ChangeEvent<HTMLTextAreaElement>) { setMessage(event.target.value); setError('') }

  return <div className={c.businessConversation} style={{ paddingBottom: composer.height }} data-business-questions>
    <ol className={c.answerHistory} aria-label="Your business context">
      {BUSINESS_SETUP_QUESTIONS.filter(q => answers[q.key].trim() && q.key !== section).map(q => <li key={q.key} className={c.savedTurn}>
        <p className={c.historyQuestion}>{q.question}</p><div className={c.historyReply}><p>{answers[q.key]}</p><button type="button" className={c.editAnswer} value={q.key} onClick={editAnswer} disabled={locked}>Edit</button></div>
      </li>)}
    </ol>
    <div className={c.memoryTurn} ref={currentTurn} data-current-question tabIndex={-1}>
      {reply && <p className={c.support}><StreamedText text={reply} /></p>}
      {question ? <>
        <BusinessQuestion text={question.question} />
        {!question.required && <button type="button" className={c.back} onClick={skipOptional} disabled={locked}>Nothing else for now →</button>}
      </> : <>
        <h2 className={c.memoryQuestion}>That’s enough to get started.</h2>
        <p className={c.support}>Ten Four will remember this about your business. You can add more or change it anytime.</p>
        <button type="button" className={c.finishConversation} onClick={onContinue} disabled={!ready || locked}>{saving ? 'Saving…' : 'Looks good'} <span aria-hidden="true">→</span></button>
      </>}
    </div>
    {createPortal(<div className={c.composerDock} ref={composer.dock}>
      {(voice.live || voice.pending || busy || uploads.progress) && <div className={c.composerNote} role="status">{uploads.progress || (voice.live ? 'Listening. Stop when you’re done, then send your reply.' : voice.pending ? 'Writing your words…' : 'Ten Four is thinking…')}</div>}
      {error && <p className={c.error} role="alert">{error}</p>}
      {uploads.error && <p className={`${c.error} ${c.uploadErrors}`} role="alert">{uploads.error}</p>}
      {!!documents.length && <ul className={c.documentList} aria-label="Business documents">{documents.map(doc => <li key={doc.name}><FileText size={16} aria-hidden="true" /><span title={doc.name}>{doc.name}</span><button type="button" value={doc.name} onClick={uploads.remove} disabled={locked} aria-label={`Remove ${doc.name}`}><X size={16} aria-hidden="true" /></button></li>)}</ul>}
      <form className={c.memoryComposer} onSubmit={send}>
        <div className={c.uploadControl}>
          <details ref={uploadMenu} className={c.uploadMenu}><summary aria-label="Add documents" aria-disabled={locked} onClick={locked ? preventOpen : undefined}><Paperclip size={22} aria-hidden="true" /></summary><div className={c.uploadOptions}><button type="button" onClick={chooseFiles} disabled={locked}><FileText size={18} />Upload documents</button><button type="button" onClick={chooseFolder} disabled={locked}><FolderOpen size={18} />Upload a folder</button><p>PDF, Word, TXT, Markdown, CSV or JSON. Up to 20 files, 5 MB each.</p></div></details>
          <input ref={filesInput} type="file" accept={BUSINESS_SETUP_FILE_ACCEPT} multiple hidden onChange={uploads.select} />
          <input ref={folderInput} type="file" multiple hidden {...{ webkitdirectory: '', directory: '' }} onChange={uploads.select} />
        </div>
        <label htmlFor="business-reply" className={c.srOnly}>Tell Ten Four</label>
        <textarea id="business-reply" ref={input} value={message} onChange={changeMessage} onKeyDown={keyDown} maxLength={4000} rows={2} placeholder="Tell Ten Four…" disabled={busy || saving || !!uploads.progress} />
        <ScribeButton live={voice.live} disabled={busy || saving || voice.pending || !!uploads.progress} onClick={voice.toggle} />
        <ComposerSendButton type="submit" label="Send answer" disabled={(!message.trim() && !documents.length) || locked} disabledArtwork="dim" />
      </form>
      <div className={c.composerFooter}><button type="button" className={c.back} onClick={openSkip} disabled={locked}>Finish later <span aria-hidden="true">↗</span></button></div>
    </div>, document.body)}
    {createPortal(<ConfirmDialog open={skipOpen} title="Finish getting set up later?" message={<><p>This should only take a few minutes. These details help Ten Four understand your work and look after your customers.</p><p>You can skip for now. You’ll need to finish this before your number can activate.</p></>} cancelLabel="Keep going" confirmLabel="Finish later" onCancel={closeSkip} onConfirm={confirmSkip} />, document.body)}
  </div>
}

function preventOpen(event: React.MouseEvent) { event.preventDefault() }
